/**
 * Pure intra-step reveal/annotation timing — the SINGLE source of truth for
 * word-timed reveals, imported by both the teach-studio (authoring preview)
 * and the classroom player so a lesson plays the SAME everywhere.
 * Element/annotation visibility windows are `[triggerMs ?? 0, endMs ?? ∞)`
 * against the step's narration clock.
 *
 * Also home to the authoring-side word-snap helpers (`wordBoundaries`,
 * `snapMsToWord`) so the timeline's snap grid and the playback clock can
 * never disagree about where a word boundary is.
 */

import type { Annotation, DrawStroke, RevealStep, Spotlight } from '../schema';

/** A per-word alignment entry (seconds), as returned by the align TTS. */
export interface WordTiming {
  word: string;
  start: number;
  end: number;
  score?: number;
}

/** Default snap radius (ms) when dragging a clip edge near a word boundary. */
export const DEFAULT_SNAP_MS = 120;

function isVisibleAt(nowMs: number, triggerMs?: number, endMs?: number): boolean {
  const start = triggerMs != null && triggerMs > 0 ? triggerMs : 0;
  const end = endMs != null ? endMs : Number.POSITIVE_INFINITY;
  return nowMs >= start && (end === Number.POSITIVE_INFINITY || nowMs < end);
}

/**
 * Sorted, de-duplicated list of word-boundary times (ms) for a step's words —
 * the snap grid the timeline drags clips against. Each word contributes its
 * `start` and `end`; an empty/absent words array yields `[]`.
 */
export function wordBoundaries(words?: WordTiming[]): number[] {
  if (!words || words.length === 0) return [];
  const set = new Set<number>();
  for (const w of words) {
    if (Number.isFinite(w.start)) set.add(Math.round(w.start * 1000));
    if (Number.isFinite(w.end)) set.add(Math.round(w.end * 1000));
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Snap a raw ms time to the nearest word boundary within `thresholdMs`,
 * **edge-aware** so shared boundaries (a word's end == the next word's start)
 * are never ambiguous: a clip's START handle snaps to word *starts*, its END
 * handle to word *ends*. Returns the snapped ms + the word index it bound to
 * (recorded as the `startWordIndex` / `endWordIndex` authoring hint), or `null`
 * when nothing is close enough (the caller keeps the free ms value).
 */
export function snapMsToWord(
  ms: number,
  words: WordTiming[] | undefined,
  edge: 'start' | 'end' = 'start',
  thresholdMs: number = DEFAULT_SNAP_MS,
): { ms: number; wordIndex: number; edge: 'start' | 'end' } | null {
  if (!words || words.length === 0) return null;
  let best: { ms: number; wordIndex: number; dist: number } | null = null;
  for (let i = 0; i < words.length; i++) {
    const boundaryMs = Math.round((edge === 'start' ? words[i].start : words[i].end) * 1000);
    const dist = Math.abs(boundaryMs - ms);
    if (dist <= thresholdMs && (!best || dist < best.dist)) {
      best = { ms: boundaryMs, wordIndex: i, dist };
    }
  }
  if (!best) return null;
  return { ms: best.ms, wordIndex: best.wordIndex, edge };
}

/** Element ids visible at `nowMs` within one step (honours elementTimings). */
export function selectStepReveals(step: RevealStep | undefined, nowMs: number): Set<string> {
  const out = new Set<string>();
  if (!step) return out;
  const timings = step.elementTimings ?? {};
  for (const id of step.elementIds ?? []) {
    const t = timings[id];
    if (isVisibleAt(nowMs, t?.triggerMs, t?.endMs)) out.add(id);
  }
  return out;
}

/** Annotations visible at `nowMs` within one step (honours triggerMs/endMs). */
export function selectStepAnnotations(step: RevealStep | undefined, nowMs: number): Annotation[] {
  if (!step?.annotations) return [];
  return step.annotations.filter((a) => isVisibleAt(nowMs, a.triggerMs, a.endMs));
}

/** True when a step actually carries intra-step timing worth driving per-frame.
 *  Strokes and spotlights always count: they render time-dependently (draw-on,
 *  cave-in/out, endMs) even without explicit triggerMs. */
export function stepHasTiming(step: RevealStep | undefined): boolean {
  if (!step) return false;
  const hasElTimings = !!step.elementTimings && Object.keys(step.elementTimings).length > 0;
  const hasAnnoTimings = (step.annotations ?? []).some((a) => a.triggerMs != null || a.endMs != null);
  const hasStrokes = (step.drawStrokes ?? []).length > 0;
  const hasSpotlights = (step.spotlights ?? []).length > 0;
  return hasElTimings || hasAnnoTimings || hasStrokes || hasSpotlights;
}

/**
 * Cumulative visible element ids across a played-so-far sequence of steps.
 * Prior steps (index < currentIndex) are evaluated at `nowMs = ∞` (elapsed);
 * the current step is evaluated at `currentNowMs`.
 */
export function selectVisibleElementIds(
  steps: RevealStep[],
  currentIndex: number,
  currentNowMs: number,
): Set<string> {
  const out = new Set<string>();
  for (let i = 0; i <= currentIndex && i < steps.length; i++) {
    const at = i === currentIndex ? currentNowMs : Number.POSITIVE_INFINITY;
    for (const id of selectStepReveals(steps[i], at)) out.add(id);
  }
  return out;
}

/** Cumulative visible annotations across played-so-far steps (mirrors {@link selectVisibleElementIds}). */
export function selectVisibleAnnotations(
  steps: RevealStep[],
  currentIndex: number,
  currentNowMs: number,
): Annotation[] {
  const out: Annotation[] = [];
  for (let i = 0; i <= currentIndex && i < steps.length; i++) {
    const at = i === currentIndex ? currentNowMs : Number.POSITIVE_INFINITY;
    for (const a of selectStepAnnotations(steps[i], at)) out.push(a);
  }
  return out;
}

/** Pen/marker strokes visible at `nowMs` within one step (honours triggerMs/endMs). */
export function selectStepStrokes(step: RevealStep | undefined, nowMs: number): DrawStroke[] {
  if (!step?.drawStrokes) return [];
  return step.drawStrokes.filter((s) => isVisibleAt(nowMs, s.triggerMs, s.endMs));
}

/** Cumulative visible strokes across played-so-far steps. */
export function selectVisibleStrokes(
  steps: RevealStep[],
  currentIndex: number,
  currentNowMs: number,
): DrawStroke[] {
  const out: DrawStroke[] = [];
  for (let i = 0; i <= currentIndex && i < steps.length; i++) {
    const at = i === currentIndex ? currentNowMs : Number.POSITIVE_INFINITY;
    for (const s of selectStepStrokes(steps[i], at)) out.push(s);
  }
  return out;
}

/** Spotlights visible at `nowMs` within one step (honours triggerMs/endMs). */
export function selectStepSpotlights(step: RevealStep | undefined, nowMs: number): Spotlight[] {
  if (!step?.spotlights) return [];
  return step.spotlights.filter((s) => isVisibleAt(nowMs, s.triggerMs, s.endMs));
}

/** Cumulative visible spotlights across played-so-far steps. */
export function selectVisibleSpotlights(
  steps: RevealStep[],
  currentIndex: number,
  currentNowMs: number,
): Spotlight[] {
  const out: Spotlight[] = [];
  for (let i = 0; i <= currentIndex && i < steps.length; i++) {
    const at = i === currentIndex ? currentNowMs : Number.POSITIVE_INFINITY;
    for (const s of selectStepSpotlights(steps[i], at)) out.push(s);
  }
  return out;
}
